import type { FirestoreTimestamp } from './timestamp'

export type EventType = 'festival' | 'party'

export interface Venue {
  id: string
  name: string
  city: string
  state?: string
  address?: string
  country?: string
  timezone?: string
  /** Stamped by the stampVenueUpdatedAt trigger on every change. Absent on docs untouched since it shipped. */
  updatedAt?: FirestoreTimestamp
}

export interface Artist {
  id: string
  name: string
  genres: string[]
  imageUrl: string
  /**
   * Saliency-detected crop anchor for `imageUrl`, as integer percentages (0–100).
   * Written only by compressArtistImage / detectEventFocalPoint / backfill-focal-points,
   * which recompute or delete it whenever imageUrl changes. Missing → each surface's own anchor.
   */
  imageFocalX?: number
  imageFocalY?: number
  memberArtistIds?: string[]
  /** Stamped by the stampArtistUpdatedAt trigger on every change. Absent on docs untouched since it shipped. */
  updatedAt?: FirestoreTimestamp
}

export interface ArtistDetails {
  id: string
  shortBio?: string
  bio?: string
  country?: string
  socialLinks?: {
    instagram?: string
    soundcloud?: string
    bandcamp?: string
    website?: string
  }
  sources?: {
    ra?: { url: string; fetchedAt: FirestoreTimestamp }
    /**
     * Last RA search for this artist, by scrape-ra-artists.ts. Scoped scrapes skip
     * the artist while it is fresh — routine containers start with no RA snapshot,
     * so without it every lineup artist is re-searched every run. Admin-SDK only.
     */
    raLookup?: { checkedAt: FirestoreTimestamp; outcome: 'matched' | 'pending' | 'no_match' }
  }
  pendingRaMatch?: {
    raSlug: string
    raName: string
    matchType: 'fuzzy' | 'substring'
    similarity: number
    candidate: {
      imageUrl?: string
      shortBio?: string
      bio?: string
      country?: string
      socialLinks?: {
        instagram?: string
        soundcloud?: string
        bandcamp?: string
        website?: string
      }
    }
    submittedAt: FirestoreTimestamp
  }
  rejectedRaSlugs?: string[]
  /**
   * The enrich-artists genre stage's last verdict on an artist it could not
   * give genres: nothing usable found, or a call web verification couldn't
   * confirm. The stage skips the artist until `checkedAt` is stale. Cleared
   * once genres are written. Admin-SDK only. See plans/artist-genre-backfill.md.
   */
  genreReview?: {
    status: 'none'
    reason?: string
    checkedAt: FirestoreTimestamp
  }
}

/** How well an event matches Beacon's electronic / dance audience — see Event.beaconFit. */
export type BeaconFit = 'core' | 'adjacent' | 'off'

export interface Event {
  id: string
  type: EventType
  name: string
  location?: string
  venueId?: string
  venueName?: string
  startDate: string
  endDate?: string
  imageUrl?: string
  /**
   * Saliency-detected crop anchor for `imageUrl`, as integer percentages (0–100).
   * Written only by compressArtistImage / detectEventFocalPoint / backfill-focal-points,
   * which recompute or delete it whenever imageUrl changes. Missing → each surface's own anchor.
   */
  imageFocalX?: number
  imageFocalY?: number
  parentEventId?: string
  description?: string
  mapImageUrl?: string
  /**
   * Set when `mapImageUrl` is a previous edition's map standing in for this
   * one's: inherited by seeding, or a "new" map that the pixel comparison showed
   * is the same image. The id of that edition. Cleared once this edition's own
   * map lands. Missing → the map (if any) is this edition's.
   */
  mapImageFromEventId?: string
  tags?: string[]
  price?: string
  ageRestriction?: string
  organizer?: string
  hasSchedule?: boolean
  /**
   * When the event or its lineup, sets or stages last changed — stamped by
   * trigger. Clients compare it via `eventContentVersion()`; queryable, so
   * "changed since T" reads only deltas.
   */
  contentUpdatedAt?: FirestoreTimestamp
  // Denormalized interest counts, recomputed periodically server-side by the
  // recomputeEventInterestCounts scheduled function. Used to rank parties by
  // popularity without reading the whole eventInterests collection. Eventually
  // consistent (updated every 6h, not per toggle). Missing → treat as 0
  // (offline/old cached docs may lack them until Firestore re-syncs).
  goingCount?: number
  interestedCount?: number
  /**
   * Claude's prior on the event's draw, 0–100 on one global scale (not
   * region-relative — compute percentiles at ranking time). Written by the
   * seed-events `score` stage; curatorScoreVersion is the rubric version that
   * produced it. A ranking signal alongside the interest counts. Missing → not
   * scored yet; treat as unknown, never as 0.
   */
  curatorScore?: number
  curatorScoreVersion?: number
  /**
   * Whether the event is what Beacon's audience comes for, judged alongside
   * curatorScore by the seed-events `score` stage. Ranked surfaces (Popular,
   * For you) drop 'off' and discount 'adjacent'; lists and search ignore it.
   * Missing → not classified yet; treat as 'core', never hide it.
   */
  beaconFit?: BeaconFit
  lineupMode?: 'stage' | 'day'
  urls?: {
    website?: string
    instagram?: string
    spotifyPlaylist?: string
    tickets?: string
  }
  published?: boolean
  needsReview?: boolean
  reviewReasons?: string[]
  // Set by the detectCancelledEvents scheduled function when the source listing
  // retitles itself `[CANCELED] …`, or by an admin. Distinct from `published`:
  // a cancelled event stays visible so anyone who has it in their plans sees it
  // struck through rather than silently vanishing. Missing → not cancelled
  // (old cached docs lack the field until Firestore re-syncs).
  cancelled?: boolean
  /** Why we believe it's cancelled — the source's own title, for an admin to check. */
  cancelledReason?: string
  /**
   * Festival-only: the crowd carries totems (Shambhala, EDC, Lost Lands).
   * Set by an admin or by seeding (set-only — seeding never clears it).
   * Missing → not a totem festival; drives the festival-mode totem upsell.
   */
  totemCulture?: boolean
  /**
   * Earliest and latest `day` across the event's sets (YYYY-MM-DD), maintained
   * by the updateEventSetDayRange trigger. Pre-parties and late closing nights
   * run outside startDate/endDate, so these widen the window festival mode
   * treats as live — read through `festivalWindow()` in shared/dates.ts, never
   * directly. Missing → no sets, or not backfilled yet.
   */
  firstSetDay?: string
  lastSetDay?: string
  /**
   * Hour (0–23) the event's festival day turns over: set times before it are
   * the previous night past midnight. Derived from the sets by
   * `deriveDayCutoff()` and written by seed-drafts' finalize step (and the
   * backfill-day-cutoff script). Read through `eventDayCutoff()`, never
   * directly. Missing → no set times, or not backfilled yet (falls back to 10).
   */
  dayCutoffHour?: number
  /**
   * Metro region id from shared/regions.ts, stamped by the stampEventRegion
   * trigger so discovery queries can filter on it. A cache of the venue/location
   * derivation, so read it through `eventRegionId()`, never directly. Missing →
   * a draft, an unresolvable location, or not stamped yet.
   */
  regionId?: string
  /**
   * Shared by every edition of the same recurring festival (`shambhala-2026`,
   * `shambhala-music-festival-2027` → `shambhala`) — a flat key, not a parent
   * event. Written by seeding's series link step and the backfill-event-series
   * script (scripts/seeding/event-series.ts). Missing → a one-off, or not
   * linked yet; never assume an event has no other editions from its absence.
   */
  seriesId?: string
}

// Per-event interest aggregates, written server-side by the
// recomputeEventInterestCounts scheduled function and read-only everywhere
// else. Derived data — never a source of truth, and eventually consistent
// (rebuilt every 6h from the interest collections, not on every toggle).
//
// Set counts live here rather than on `sets/{setId}` because any write to a set
// stamps the event's contentUpdatedAt, which would invalidate every client's cached
// event detail once an hour. One doc per event also keeps a reader at one extra
// fetch per event page instead of one per set.
//
// Only written for published events that haven't been over for long — trending
// is meaningless for a finished event, and a publicly readable stats doc for an
// unpublished event would leak its existence.
export interface EventStats {
  /** Always equal to the event's ID — the doc is keyed by it. */
  id: string
  // Mirrored from the event doc so a reader holding stats needs only one doc.
  // They stay on the event too, where list views rank without a read per card.
  goingCount: number
  interestedCount: number
  /** setId → interest split. Sets nobody is interested in are omitted. */
  sets: Record<string, { going: number; interested: number }>
  updatedAt: FirestoreTimestamp
}

export interface Stage {
  id: string
  name: string
  imageUrl?: string
  description?: string
  order?: number
}

export interface LineupEntry {
  id: string
  eventId: string
  artistId: string
  stageId?: string
  day?: string
  tier?: number
  note?: string
  /** Performers of the act, billing order; `artistIds[0] == artistId`. Absent on solo/legacy rows — read via `entryArtistIds()`. */
  artistIds?: string[]
  /** The announced billing when it says more than the performers' names ("Fury with MC Dino", "Skull Machine"). */
  title?: string
  /** The named-collab artist (has `memberArtistIds`) this act performs as; its identity when present — see `actKey()`. */
  collabArtistId?: string
}

export interface Set {
  id: string
  eventId: string
  stageId?: string
  day: string
  startTime: string
  endTime: string
  artistIds: string[]
  title?: string
  /** Artist-less slots only: the festival's own copy for the slot. Seed-only, untranslated; isValidSet caps it at 1000. */
  description?: string
  /**
   * A slot published before its act was named ("Secret Takeover"). Revealed by
   * updating this same doc, so setInterests / setPosts / totemSlots survive.
   */
  placeholder?: boolean
  note?: string
  /**
   * Server-stamped by stampSetScheduleChange when the set's day, time or stage
   * last moved (not when its times are first published). Absent on sets that
   * never moved. Drives the schedule's "Updated" chip.
   */
  scheduleChangedAt?: FirestoreTimestamp
  /** The named-collab artist these performers play as (Skull Machine); `artistIds` still lists the members. */
  collabArtistId?: string
}

export interface ScheduleSettings {
  id: string
  userId: string
  eventId: string
  stageOrder: string[]
  hiddenStages: string[]
  createdAt: FirestoreTimestamp
  updatedAt: FirestoreTimestamp
}

export const SCHEDULE_MARKER_LABEL_MAX = 60
export const SCHEDULE_MARKER_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

/**
 * A labeled line on a schedule grid (#33). `day` + `time` follow the set
 * convention: times before the event's day cutoff belong to `day`'s night.
 * The audience fields follow PregameAudience (both absent = the author's whole
 * crew) and only apply when `isPrivate` is false. `viewerIds` is that audience
 * expanded, written only by the syncScheduleMarkerViewers trigger.
 */
export interface ScheduleMarker {
  id: string
  userId: string
  eventId: string
  day: string
  time: string
  label: string
  isPrivate: boolean
  audienceGroupIds?: string[]
  audienceFriends?: boolean
  viewerIds: string[]
  createdAt: FirestoreTimestamp
  updatedAt: FirestoreTimestamp
}

/** users/{uid}/private/scheduleMarkerHides — other people's lines (and whole authors) the viewer hid. */
export interface ScheduleMarkerHides {
  markerIds?: string[]
  authorIds?: string[]
}

/** users/{uid}/private/mapPinHides — other people's map pins (and whole authors' pins, on every map) the viewer hid. */
export interface MapPinHides {
  pinIds?: string[]
  authorIds?: string[]
}

export const MAP_PIN_LABEL_MAX = 40
export const MAP_PIN_NOTE_MAX = 200
/** How long an ephemeral ("we're here right now") pin stays on the map. */
export const MAP_PIN_TTL_MS = 2 * 60 * 60 * 1000

export type MapPinKind = 'persistent' | 'ephemeral'

/**
 * A spot someone dropped on a festival's venue map (#334): camp, the meetup
 * tree, "we're by the sound booth". `x`/`y` are fractions (0–1) of the map
 * image itself, not of the screen, so a pin survives any zoom, viewport or a
 * higher-res copy of the same map. Audience works like ScheduleMarker:
 * private, or shared with the author's crew (`viewerIds`, trigger-written).
 * An ephemeral pin carries `expiresAt` and stops showing once it passes.
 */
export interface MapPin {
  id: string
  userId: string
  eventId: string
  x: number
  y: number
  label: string
  emoji?: string
  note?: string
  kind: MapPinKind
  expiresAt?: FirestoreTimestamp
  isPrivate: boolean
  audienceGroupIds?: string[]
  audienceFriends?: boolean
  viewerIds: string[]
  createdAt: FirestoreTimestamp
  updatedAt: FirestoreTimestamp
}
